'use client'

import { Button } from '@/components/ui/button'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRef } from 'react'
import { CareAndShareContainer } from './container'

export const CareShareJourney = () => {
  const t = useTranslations()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleIframeLoad = () => {
    try {
      const iframeElement = iframeRef.current
      if (!iframeElement) return

      const iframeDocument =
        iframeElement.contentDocument || iframeElement.contentWindow?.document

      if (iframeDocument) {
        const hideElementsAndInjectCSS = () => {
          // 1. Ensure the CSS style block is present in the <head>
          if (!iframeDocument.getElementById('hidden-iframe-styles')) {
            const style = iframeDocument.createElement('style')
            style.id = 'hidden-iframe-styles'
            style.textContent = `
              header,
              footer,
              [role="presentation"],
              [role="button"],
              [ga-tracking-value="contact-fab"],
              #chat-widget-container,
              .zalo-chat-widget,
              .fb-customerchat {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
                pointer-events: none !important;
                height: 0 !important;
                width: 0 !important;
                position: absolute !important;
                z-index: -9999 !important;
              }
            `
            iframeDocument.head.appendChild(style)
          }

          // 2. Force inline inline styles directly on the DOM nodes just in case they override our CSS
          const targets = iframeDocument.querySelectorAll(
            'header, footer, [role="presentation"], [role="button"], [ga-tracking-value="contact-fab"]'
          )
          targets.forEach((el) => {
            ;(el as HTMLElement).style.setProperty(
              'display',
              'none',
              'important'
            )
          })
        }

        // Inject immediately
        hideElementsAndInjectCSS()

        // Maintain the injection with an interval.
        // Next.js hydration or third-party chat widgets might clear the DOM or mount *after* the initial iframe load.
        const intervalId = setInterval(hideElementsAndInjectCSS, 500)

        // Clear the interval after 15 seconds to avoid memory leaks once the page stabilized
        setTimeout(() => clearInterval(intervalId), 15000)

        // Initialize IntersectionObserver to scroll when the iframe is visible
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting && iframeElement.contentWindow) {
              const targetElement = iframeDocument.getElementById(
                'minh-bach-quy-care-and-share-content'
              )
              if (targetElement) {
                // Use scrollTo on the iframe window to avoid scrolling the parent page
                const top =
                  targetElement.getBoundingClientRect().top +
                  iframeElement.contentWindow.pageYOffset -
                  20 // Optional offset for better alignment

                iframeElement.contentWindow.scrollTo({
                  top,
                  behavior: 'smooth',
                })

                // Disconnect after first scroll to avoid jumping back if scrolled manually
                observer.disconnect()
              }
            }
          },
          { threshold: 0.2 } // Trigger when 20% of iframe is visible
        )

        if (containerRef.current) {
          observer.observe(containerRef.current)
        }
      }
    } catch (error) {
      console.error('Error injecting CSS into iframe:', error)
    }
  }

  return (
    <>
      <CareAndShareContainer className='lg:pt-0'>
        <h2 className='mb-0 font-criteria text-lg font-medium uppercase leading-6 text-primary-400 lg:text-xl lg:leading-13'>
          {t('care_and_share_hardcode.journey.title')}
        </h2>
        <div
          ref={containerRef}
          className='no-scrollbar mt-10 max-h-[700px] overflow-y-auto overflow-x-hidden rounded-[32px] border-2 border-neutral-200 md:container lg:mx-auto'
        >
          <iframe
            ref={iframeRef}
            src='/hanh-trinh-care-and-share'
            width='100%'
            height='700'
            className='w-full rounded-[32px] border-0'
            title={t(
              'care_and_share_hardcode.fund_activities.statement.image_alt'
            )}
            loading='lazy'
            allow='fullscreen'
            onLoad={handleIframeLoad}
          />
        </div>

        <div className='mt-10 flex w-full justify-center'>
          <Link href='/hanh-trinh-care-and-share'>
            <Button size='large' variant='outline'>
              {t('care_and_share_hardcode.journey.button')}
            </Button>
          </Link>
        </div>
      </CareAndShareContainer>
    </>
  )
}
